/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX, ReactNode } from "react";

import { Skeleton } from "../Skeleton";

import "./ContentGrid.scss";

/** Placeholder rows drawn by an unbounded grid with no explicit preference. */
const DEFAULT_SKELETON_ROWS: number = 3;

/**
 * Ceiling on placeholder rows.
 *
 * A grid that scrolls can legitimately declare a large `visibleRows`, and every
 * placeholder past the fold costs a DOM node and an animation for something
 * nobody sees — an expense the SmartTV baseline in particular cannot spare.
 */
const MAX_SKELETON_ROWS: number = 12;

/**
 * How many placeholder rows to draw for a first load.
 *
 * Defaults to filling the viewport, because the point of a first-load skeleton
 * is to occupy the space the real rows are about to occupy: fewer rows and the
 * page grows underneath the user when the data lands, more and it shrinks.
 *
 * @param requested explicit caller override, if any.
 * @param visibleRows rows on screen at once, or `0` when unbounded.
 * @param maxRows hard cap on rows of data, if the caller set one.
 */
export const resolveSkeletonRowCount = (
  requested: number | undefined,
  visibleRows: number,
  maxRows: number | undefined
): number => {
  // A grid with a clipped viewport wants exactly that many rows; an unbounded
  // one has no viewport to fill, so it falls back to a sensible handful.
  const intrinsic: number =
    visibleRows > 0 ? visibleRows : DEFAULT_SKELETON_ROWS;
  const preferred: number =
    requested !== undefined && requested > 0 ? requested : intrinsic;

  // A grid capped at two rows of data must not promise five.
  const capped: number =
    maxRows !== undefined && maxRows > 0
      ? Math.min(preferred, maxRows)
      : preferred;

  return Math.max(1, Math.min(capped, MAX_SKELETON_ROWS));
};

export interface ContentGridSkeletonCellsProps {
  /** Number of placeholder cells to draw. Values below 1 render nothing. */
  count: number;
  /**
   * Flat index the first cell stands at. Drives the pulse stagger, so passing
   * the true grid index makes the shimmer travel across and down the wall
   * rather than restarting on every row.
   */
  startIndex: number;
  /**
   * Cell body, by flat index. Omit for a plain block filling the cell; supply
   * it when the real cell has a silhouette worth previewing (a poster above two
   * lines of metadata, say).
   */
  renderItem?: (index: number) => ReactNode;
}

/**
 * Placeholder cells for {@link ContentGrid}.
 *
 * Cells only — they are mounted inside a `.content-grid__row`, which is what
 * lets them inherit the exact width, height and gap the real cells get from the
 * caller's `metrics`. There is no second set of dimensions to keep in sync:
 * the loading state and the loaded state are laid out by the same rules.
 */
export const ContentGridSkeletonCells: FunctionComponent<
  ContentGridSkeletonCellsProps
> = (props: ContentGridSkeletonCellsProps): JSX.Element => {
  const { count, startIndex, renderItem } = props;

  return (
    <>
      {Array.from(
        { length: Math.max(count, 0) },
        (_: unknown, offset: number): JSX.Element => {
          const index: number = startIndex + offset;
          return (
            <div
              key={`content-grid-skeleton-${index}`}
              className="content-grid__cell content-grid__cell--skeleton"
              aria-hidden="true"
            >
              {renderItem === undefined ? (
                <Skeleton sequence={index} className="content-grid__skeleton" />
              ) : (
                (renderItem(index) as ReactNode)
              )}
            </div>
          );
        }
      )}
    </>
  );
};

export interface ContentGridSkeletonRowProps extends ContentGridSkeletonCellsProps {
  /** Row index this placeholder row stands at, surfaced for tests and debugging. */
  row: number;
}

/** A full row of placeholder cells, in the same wrapper a real row uses. */
export const ContentGridSkeletonRow: FunctionComponent<
  ContentGridSkeletonRowProps
> = (props: ContentGridSkeletonRowProps): JSX.Element => {
  const { row, count, startIndex, renderItem } = props;

  return (
    <div className="content-grid__row" data-skeleton-row={row}>
      <ContentGridSkeletonCells
        count={count}
        startIndex={startIndex}
        renderItem={renderItem}
      />
    </div>
  );
};
